<template>
  <div class="app-hub">
    <PageHeader
      :pageName="$t('appHub.pageName')"
      :pageTitle="$t('appHub.pageTitle')"
      :pageSubtitle="$t('appHub.greeting', { name: userName })"
    >
      <template #actions>
        <!-- Optionnel: Ajouter un bouton général ici si besoin -->
      </template>
    </PageHeader>

    <div class="app-hub-content">
      <!-- Hero : invitation à créer un agent -->
      <section class="hero-banner">
        <div class="hero-glow hero-glow-1"></div>
        <div class="hero-glow hero-glow-2"></div>

        <div class="hero-content">
          <span class="hero-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3z"/>
            </svg>
            {{ $t('appHub.assistantBadge') }}
          </span>
          <h2 class="hero-title">{{ $t('appHub.heroTitle') }}</h2>
          <p class="hero-subtitle">
            {{ $t('appHub.heroSubtitle') }}
          </p>
          <div class="hero-actions">
            <button class="hero-cta" @click="createAgent">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {{ $t('appHub.createAgent') }}
            </button>
            <button class="hero-cta-ghost" @click="handleAction({ action: 'route', route: '/dashboard/chatbots' })">
              {{ $t('appHub.viewAgents') }}
            </button>
          </div>
        </div>

        <div class="hero-visual" aria-hidden="true">
          <div class="hero-orb">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="10" rx="2"/>
              <circle cx="12" cy="5" r="2"/>
              <path d="M12 7v4"/>
              <line x1="8" y1="16" x2="8.01" y2="16"/>
              <line x1="16" y1="16" x2="16.01" y2="16"/>
            </svg>
          </div>
        </div>
      </section>

      <!-- En-tête de section -->
      <div class="section-heading">
        <h3>{{ $t('appHub.sectionTitle') }}</h3>
        <p>{{ $t('appHub.sectionSubtitle') }}</p>
      </div>

      <!-- Grille des fonctionnalités -->
      <div class="features-grid">
        <button
          v-for="(feature, index) in features"
          :key="index"
          class="feature-card"
          :class="{ 'pro-locked': feature.proOnly && !isPro }"
          @click="handleAction(feature)"
        >
          <span v-if="feature.proOnly && !isPro" class="pro-badge">Pro</span>
          <div class="feature-icon" :class="feature.colorClass">
            <span v-html="feature.icon"></span>
          </div>

          <div class="feature-text">
            <h4>{{ feature.title }}</h4>
            <p>{{ feature.description }}</p>
          </div>

          <div class="feature-arrow">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </div>
        </button>
      </div>
    </div>

    <!-- Modal pour sélectionner un agent (Griot AI, Playground, Broadcast) -->
    <Teleport to="body">
      <div v-if="showAgentSelectionModal" class="modal-overlay" :class="{ 'dark-mode': isDarkMode }" @click.self="closeAgentModal">
        <div class="modal-content premium-modal">
          <div class="modal-header">
            <h3>{{ $t('appHub.modalTitle') }}</h3>
            <button class="close-btn" @click="closeAgentModal">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" stroke-opacity="0.1" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="search-container">
               <div class="search-input-wrapper">
                  <input
                    type="text"
                    v-model="agentSearchQuery"
                    :placeholder="$t('appHub.searchPlaceholder')"
                    class="premium-search-input"
                  >
                  <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                     <circle cx="11" cy="11" r="8"></circle>
                     <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
               </div>
            </div>

            <p class="modal-subtitle">{{ $t('appHub.availableAgents') }}</p>

            <div v-if="loadingAgents" class="loading-state">
              <div class="spinner"></div>
              {{ $t('appHub.loading') }}
            </div>

            <div v-else class="agents-list premium-list">
              <div
                v-for="agent in filteredAgentList"
                :key="agent._id"
                class="agent-item premium-item"
              >
                  <div class="agent-avatar-wrapper">
                   <div class="agent-avatar">
                      <img v-if="getChatbotAvatar(agent)" :src="getChatbotAvatar(agent)" :alt="agent.bot_name" class="avatar-img" @error="handleAvatarError($event, agent)" />
                      <span v-else>{{ agent.bot_name.charAt(0).toUpperCase() }}</span>
                   </div>
                   <div class="agent-status-dot" :class="agent.status === 'active' ? 'active' : 'inactive'"></div>
                </div>

                <div class="agent-info">
                  <span class="agent-name">{{ agent.bot_name }}</span>
                  <span v-if="targetAction === 'broadcast'" class="agent-phone-chip">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"></rect><line x1="11" y1="18" x2="13" y2="18"></line></svg>
                    {{ agent.display_phone_number || agent.phone_number_id || 'Numéro non configuré' }}
                  </span>
                  <span v-else class="agent-email">{{ agent.email || $t('appHub.noEmail') }}</span>
                </div>

                <button class="select-agent-btn" @click="selectAgentForAction(agent._id)">
                  {{ $t('appHub.select') }}
                </button>
              </div>

              <div v-if="filteredAgentList.length === 0" class="empty-search">
                 {{ $t('appHub.noAgent') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modale d'abonnement (fonctionnalités Pro cliquées en Free) -->
    <UpgradeModal
      :show="showUpgradeModal"
      :current-plan-slug="currentPlanSlug"
      @close="showUpgradeModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ecommerceDashboardUrl, apiBaseUrl } from '@/config';
import PageHeader from '../components/PageHeader.vue';
import UpgradeModal from '../components/UpgradeModal.vue';
import axios from 'axios';
import { syncSsoCookieFromStorage } from '@/utils/ssoCookie';

export default {
  name: 'AppHub',
  components: {
    PageHeader,
    UpgradeModal
  },
  props: {
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const userName = ref(t('appHub.userFallback'));
    const isPro = ref(false);
    const showUpgradeModal = ref(false);
    const currentPlanSlug = ref(null);

    // User logic + plan (Pro/Free) pour le gating des fonctionnalités réservées au Pro
    onMounted(async () => {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      try {
        const parsedUser = JSON.parse(storedUser);
        userName.value = parsedUser.data?.name || t('appHub.userFallback');
        const token = parsedUser.data?.token;
        if (token) {
          const { data } = await axios.get(`${apiBaseUrl}/strong/billing/my-plan`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          isPro.value = !!data?.is_pro;
          currentPlanSlug.value = data?.plan_slug || null;
        }
      } catch (e) {
        console.error(e);
      }
    });

    const features = computed(() => {
      const list = [
      {
        title: t('appHub.feat0Title'),
        description: t('appHub.feat0Desc'),
        action: 'agent-action',
        actionType: 'playground',
        colorClass: 'color-brand',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>'
      },
      {
        title: t('appHub.featChatTitle'),
        description: t('appHub.featChatDesc'),
        action: 'external',
        url: 'agenttalk-user',
        proOnly: true,
        colorClass: 'color-brand',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>'
      },
      {
        title: t('appHub.feat1Title'),
        description: t('appHub.feat1Desc'),
        action: 'route',
        route: '/dashboard/chatbots',
        colorClass: 'color-brand',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8.01" y2="16"/><line x1="16" y1="16" x2="16.01" y2="16"/></svg>'
      },
      {
        title: t('appHub.feat2Title'),
        description: t('appHub.feat2Desc'),
        action: 'subscribers',
        colorClass: 'color-green',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100-8 4 4 0 000 8z" /></svg>'
      },
      {
        title: t('appHub.feat3Title'),
        description: t('appHub.feat3Desc'),
        action: 'agent-action',
        actionType: 'widget',
        proOnly: true,
        colorClass: 'color-brand',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>'
      },
      {
        title: t('appHub.feat4Title'),
        description: t('appHub.feat4Desc'),
        action: 'route',
        route: '/dashboard/whatsapp-signup',
        proOnly: true,
        colorClass: 'color-whatsapp',
        icon: '<svg width="24" height="24" viewBox="0 0 32 32" fill="currentColor"><path d="M26.576 5.363c-2.69-2.69-6.406-4.354-10.511-4.354-8.209 0-14.865 6.655-14.865 14.865 0 2.732 0.737 5.291 2.022 7.491l-0.038-0.070-2.109 7.702 7.879-2.067c2.051 1.139 4.498 1.809 7.102 1.809h0.006c8.209-0.003 14.862-6.659 14.862-14.868 0-4.103-1.662-7.817-4.349-10.507l0 0zM16.062 28.228h-0.005c-0 0-0.001 0-0.001 0-2.319 0-4.489-0.64-6.342-1.753l0.056 0.031-0.451-0.267-4.675 1.227 1.247-4.559-0.294-0.467c-1.185-1.862-1.889-4.131-1.889-6.565 0-6.822 5.531-12.353 12.353-12.353s12.353 5.531 12.353 12.353c0 6.822-5.53 12.353-12.353 12.353h-0zM22.838 18.977c-0.371-0.186-2.197-1.083-2.537-1.208-0.341-0.124-0.589-0.185-0.837 0.187-0.246 0.371-0.958 1.207-1.175 1.455-0.216 0.249-0.434 0.279-0.805 0.094-1.15-0.466-2.138-1.087-2.997-1.852l0.010 0.009c-0.799-0.74-1.484-1.587-2.037-2.521l-0.028-0.052c-0.216-0.371-0.023-0.572 0.162-0.757 0.167-0.166 0.372-0.434 0.557-0.65 0.146-0.179 0.271-0.384 0.366-0.604l0.006-0.017c0.043-0.087 0.068-0.188 0.068-0.296 0-0.131-0.037-0.253-0.101-0.357l0.002 0.003c-0.094-0.186-0.836-2.014-1.145-2.758-0.302-0.724-0.609-0.625-0.836-0.637-0.216-0.010-0.464-0.012-0.712-0.012-0.395 0.010-0.746 0.188-0.988 0.463l-0.001 0.002c-0.802 0.761-1.3 1.834-1.3 3.023 0 0.026 0 0.053 0.001 0.079l-0-0.004c0.131 1.467 0.681 2.784 1.527 3.857l-0.012-0.015c1.604 2.379 3.742 4.282 6.251 5.564l0.094 0.043c0.548 0.248 1.25 0.513 1.968 0.74l0.149 0.041c0.442 0.14 0.951 0.221 1.479 0.221 0.303 0 0.601-0.027 0.889-0.078l-0.031 0.004c1.069-0.223 1.956-0.868 2.497-1.749l0.009-0.017c0.165-0.366 0.261-0.793 0.261-1.242 0-0.185-0.016-0.366-0.047-0.542l0.003 0.019c-0.092-0.155-0.34-0.247-0.712-0.434z"></path></svg>'
      },
      {
        title: t('appHub.feat5Title'),
        description: t('appHub.feat5Desc'),
        action: 'agent-action',
        actionType: 'broadcast',
        proOnly: true,
        colorClass: 'color-amber',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 11a9 9 0 0 1 9 9"></path><path d="M4 4a16 16 0 0 1 16 16"></path><circle cx="5" cy="19" r="1"></circle></svg>'
      },
      {
        title: t('appHub.featInstaTitle'),
        description: t('appHub.featInstaDesc'),
        action: 'route',
        route: '/dashboard/instagram',
        colorClass: 'color-instagram',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>'
      },
      {
        title: t('appHub.feat6Title'),
        description: t('appHub.feat6Desc'),
        action: 'external',
        url: 'ecommerce',
        proOnly: true,
        colorClass: 'color-terracotta',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>'
      },
      {
        title: t('appHub.feat7Title'),
        description: t('appHub.feat7Desc'),
        action: 'external',
        url: 'https://console.peelo.io/',
        proOnly: true,
        colorClass: 'color-slate',
        icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="3" /><path d="M7 8l4 4-4 4" /><path d="M13 16h4" /></svg>'
      }
      ];
      // Toutes les fonctionnalités Pro en dernier (tri stable -> ordre conservé dans chaque groupe).
      return list.sort((a, b) => (a.proOnly ? 1 : 0) - (b.proOnly ? 1 : 0));
    });

    // Agent Selection Modal logic
    const showAgentSelectionModal = ref(false);
    const agentList = ref([]);
    const loadingAgents = ref(false);
    const agentSearchQuery = ref('');
    const targetAction = ref('');

    const filteredAgentList = computed(() => {
        if (!agentSearchQuery.value) return agentList.value;
        const query = agentSearchQuery.value.toLowerCase();
        return agentList.value.filter(agent =>
            agent.bot_name.toLowerCase().includes(query) ||
            (agent.email && agent.email.toLowerCase().includes(query))
        );
    });

    const getChatbotAvatar = (agent) => {
      if (agent.avatar === '/avatars/bot1.png') {
        return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${agent._id || agent.bot_name}`;
      }
      if (agent.avatar) {
        if (agent.avatar.startsWith('http')) {
          return agent.avatar;
        }
        return `https://api.peelo.io/public/${agent.avatar}`;
      }
      return null;
    };

    const handleAvatarError = (event, agent) => {
        agent.avatar = '';
    };

    const fetchAgents = async () => {
         const userData = localStorage.getItem('user');
         if (!userData) return [];

         const token = JSON.parse(userData).data.token;

         const response = await axios.get(`${apiBaseUrl}/get/list/chatbots`, {
           headers: { 'Authorization': `Bearer ${token}` }
         });

         return response.data.chatbots || [];
    };

    const handleAgentSelectionForAction = async (action) => {
      try {
        loadingAgents.value = true;
        targetAction.value = action;

        let agents = await fetchAgents();
        // Broadcast : uniquement les agents déjà associés à un numéro WhatsApp (sinon ça prête à confusion).
        if (action === 'broadcast') {
          // Uniquement les agents avec un numéro WhatsApp VALIDE (on exclut les anciens marqués "old")
          agents = agents.filter(a => {
            const num = String(a.display_phone_number || a.phone_number_id || '');
            return num && !/old/i.test(num);
          });
        }

        if (agents.length === 0) {
            if (action === 'broadcast') router.push('/dashboard/whatsapp/broadcast');
            else if (action === 'playground') router.push('/dashboard/chatbots');
            else if (action === 'widget') router.push('/dashboard/chatbots/integration/widget');
            else if (action === 'instagram') router.push('/dashboard/chatbots');
            else router.push('/dashboard/chatbots');
        } else if (agents.length === 1) {
            const id = agents[0]._id;
            if (action === 'broadcast') router.push(`/dashboard/whatsapp/broadcast?idbot=${id}`);
            else if (action === 'playground') router.push(`/dashboard/chatbots/${id}/playground`);
            else if (action === 'widget') router.push(`/dashboard/chatbots/${id}/integration/widget`);
            else if (action === 'instagram') router.push(`/dashboard/chatbots/${id}/instagram`);
            else router.push(`/dashboard/chatbots/${id}/griot-builder`);
        } else {
            agentList.value = agents;
            showAgentSelectionModal.value = true;
        }

      } catch (error) {
        if (action === 'broadcast') router.push('/dashboard/whatsapp/broadcast');
        else if (action === 'playground') router.push('/dashboard/chatbots');
        else router.push('/dashboard/chatbots');
      } finally {
        loadingAgents.value = false;
      }
    };

    const handleSubscribersClick = async () => {
      try {
        loadingAgents.value = true;
        const agents = await fetchAgents();

        if (agents.length === 1) {
            router.push(`/dashboard/chatbots/${agents[0]._id}/home`);
        } else {
            router.push('/dashboard/chatbots');
        }
      } catch (error) {
        router.push('/dashboard/chatbots');
      } finally {
        loadingAgents.value = false;
      }
    };

    const navigateToEcommerce = () => {
      const userData = localStorage.getItem('user');
      if (!userData) {
        window.open(ecommerceDashboardUrl, '_blank');
        return;
      }

      try {
        import('@/utils/crypto.js').then(({ encryptUserData }) => {
          const encryptedData = encryptUserData(userData);
          const autoconnectUrl = `${ecommerceDashboardUrl}/#/autoconnect/${encryptedData}`;
          window.open(autoconnectUrl, '_blank');
        }).catch(error => {
          window.open(ecommerceDashboardUrl, '_blank');
        });
      } catch (error) {
        window.open(ecommerceDashboardUrl, '_blank');
      }
    };

    const createAgent = () => {
      router.push('/dashboard/chatbots/create');
    };

    const handleAction = (feature) => {
      // Fonctionnalités réservées au Pro : en Free, on ouvre la modale d'abonnement.
      if (feature.proOnly && !isPro.value) {
        showUpgradeModal.value = true;
        return;
      }
      if (feature.action === 'route') {
        router.push(feature.route);
      } else if (feature.action === 'agent-action') {
        handleAgentSelectionForAction(feature.actionType);
      } else if (feature.action === 'subscribers') {
        handleSubscribersClick();
      } else if (feature.action === 'external') {
        if (feature.url === 'ecommerce') {
          navigateToEcommerce();
        } else if (feature.url === 'agenttalk-user') {
          let uid = '';
          try { uid = JSON.parse(localStorage.getItem('user') || '{}').data?.id || ''; } catch (e) { /* ignore */ }
          window.open(`https://agenttalk.peelo.io/user/${uid}`, '_blank');
        } else {
          // SSO : dépose le cookie de session avant d'ouvrir un autre sous-domaine
          // *.peelo.io (ex: console.peelo.io) pour éviter un re-login.
          syncSsoCookieFromStorage();
          window.open(feature.url, '_blank');
        }
      }
    };

    const closeAgentModal = () => {
        showAgentSelectionModal.value = false;
    };

    const selectAgentForAction = (id) => {
        showAgentSelectionModal.value = false;
        if (targetAction.value === 'broadcast') {
            router.push(`/dashboard/whatsapp/broadcast?idbot=${id}`);
        } else if (targetAction.value === 'playground') {
            router.push(`/dashboard/chatbots/${id}/playground`);
        } else if (targetAction.value === 'widget') {
            router.push(`/dashboard/chatbots/${id}/integration/widget`);
        } else if (targetAction.value === 'instagram') {
            router.push(`/dashboard/chatbots/${id}/instagram`);
        } else {
            router.push(`/dashboard/chatbots/${id}/griot-builder`);
        }
    };

    return {
      userName,
      features,
      handleAction,
      createAgent,
      // Agent Modal
      showAgentSelectionModal,
      agentList,
      loadingAgents,
      agentSearchQuery,
      filteredAgentList,
      targetAction,
      closeAgentModal,
      selectAgentForAction,
      getChatbotAvatar,
      handleAvatarError,
      isPro,
      showUpgradeModal,
      currentPlanSlug
    };
  }
};
</script>

<style scoped>
.app-hub {
  display: flex;
  flex-direction: column;
}

.app-hub-content {
  padding: 32px;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* ============================================================
   HERO — invitation à créer un agent
   ============================================================ */
.hero-banner {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  padding: 48px 48px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  background: linear-gradient(135deg, #143028 0%, #0d2019 100%);
  box-shadow: 0 24px 60px -20px rgba(20, 48, 40, 0.55);
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.55;
  pointer-events: none;
}

.hero-glow-1 {
  width: 320px;
  height: 320px;
  background: rgba(218, 255, 209, 0.35);
  top: -120px;
  right: -60px;
}

.hero-glow-2 {
  width: 260px;
  height: 260px;
  background: rgba(218, 255, 209, 0.18);
  bottom: -140px;
  left: 10%;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 560px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(218, 255, 209, 0.14);
  border: 1px solid rgba(218, 255, 209, 0.25);
  color: var(--peelo-accent, #DAFFD1);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  margin-bottom: 18px;
}

.hero-title {
  font-size: 2.1rem;
  line-height: 1.15;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 28px;
}

.hero-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 24px;
  border: none;
  border-radius: 14px;
  background: var(--peelo-accent, #DAFFD1);
  color: var(--peelo-green, #143028);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 24px -6px rgba(218, 255, 209, 0.5);
}

.hero-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px -8px rgba(218, 255, 209, 0.6);
}

.hero-cta-ghost {
  display: inline-flex;
  align-items: center;
  padding: 13px 22px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hero-cta-ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.35);
}

.hero-visual {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
}

.hero-orb {
  width: 132px;
  height: 132px;
  border-radius: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--peelo-green, #143028);
  background: linear-gradient(145deg, #DAFFD1, #a9e8a0);
  box-shadow: 0 20px 40px -10px rgba(218, 255, 209, 0.4), inset 0 2px 6px rgba(255, 255, 255, 0.5);
  animation: float 5s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

/* ============================================================
   SECTION HEADING
   ============================================================ */
.section-heading {
  margin-bottom: 20px;
}

.section-heading h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--light-text, #143028);
  margin: 0 0 4px;
  letter-spacing: -0.01em;
}

.section-heading p {
  font-size: 14px;
  color: var(--light-text-muted, rgba(20, 48, 40, 0.7));
  margin: 0;
}

/* ============================================================
   GRILLE DES FONCTIONNALITÉS
   ============================================================ */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 18px;
}

.feature-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;
  padding: 20px;
  background: var(--light-card-bg, #ffffff);
  border: 1px solid var(--light-border-dark, rgba(20, 48, 40, 0.08));
  border-radius: 20px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 4px 18px rgba(20, 48, 40, 0.04);
  transition: transform 0.25s cubic-bezier(0.25, 0.8, 0.25, 1),
              box-shadow 0.25s ease,
              border-color 0.25s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  border-color: rgba(20, 48, 40, 0.16);
  box-shadow: 0 16px 36px -12px rgba(20, 48, 40, 0.18);
}

.feature-icon {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;
}

.feature-icon :deep(svg) {
  width: 26px;
  height: 26px;
}

.feature-card:hover .feature-icon {
  transform: scale(1.06) rotate(-3deg);
}

/* Palette harmonisée avec la charte Peelo (vert / menthe / tons terreux) */
.feature-icon.color-brand { background: rgba(20, 48, 40, 0.07); color: #143028; }
.feature-icon.color-green { background: rgba(22, 101, 70, 0.10); color: #166546; }
.feature-icon.color-whatsapp { background: rgba(37, 211, 102, 0.12); color: #1faa52; }
.feature-icon.color-amber { background: rgba(201, 133, 47, 0.12); color: #b9772a; }
.feature-icon.color-terracotta { background: rgba(184, 92, 74, 0.12); color: #b05541; }
.feature-icon.color-instagram { background: rgba(193, 53, 132, 0.12); color: #C13584; }
.feature-icon.color-slate { background: rgba(71, 92, 102, 0.10); color: #4a5e68; }

.feature-card:hover .feature-icon.color-brand { background: #143028; color: #DAFFD1; }
.feature-card:hover .feature-icon.color-green { background: #166546; color: #ffffff; }
.feature-card:hover .feature-icon.color-whatsapp { background: #1faa52; color: #ffffff; }
.feature-card:hover .feature-icon.color-amber { background: #b9772a; color: #ffffff; }
.feature-card:hover .feature-icon.color-terracotta { background: #b05541; color: #ffffff; }
.feature-card:hover .feature-icon.color-instagram { background: linear-gradient(45deg, #F58529, #DD2A7B, #8134AF); color: #ffffff; }
.feature-card:hover .feature-icon.color-slate { background: #4a5e68; color: #ffffff; }

.feature-text {
  flex: 1;
  min-width: 0;
}

.feature-text h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--light-text, #143028);
  margin: 0 0 4px;
  letter-spacing: -0.01em;
}

.feature-text p {
  font-size: 13px;
  line-height: 1.45;
  color: var(--light-text-muted, rgba(20, 48, 40, 0.65));
  margin: 0;
}

.feature-arrow {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text-faint, rgba(20, 48, 40, 0.4));
  background: var(--light-hover-bg, rgba(20, 48, 40, 0.04));
  opacity: 0;
  transform: translateX(-6px);
  transition: all 0.25s ease;
}

.feature-card:hover .feature-arrow {
  opacity: 1;
  transform: translateX(0);
  color: var(--peelo-green, #143028);
  background: var(--peelo-accent, #DAFFD1);
}

/* ============================================================
   DARK MODE
   ============================================================ */
:global(.dark-mode) .hero-banner {
  background: linear-gradient(135deg, #0d2019 0%, #06120e 100%);
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.6);
}

:global(.dark-mode) .section-heading h3 {
  color: var(--dark-text, #ffffff);
}

:global(.dark-mode) .section-heading p {
  color: var(--dark-text-muted, rgba(255, 255, 255, 0.7));
}

:global(.dark-mode) .feature-card {
  background: var(--dark-card-bg, rgba(255, 255, 255, 0.03));
  border-color: var(--dark-border-light, rgba(255, 255, 255, 0.08));
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);
}

:global(.dark-mode) .feature-card:hover {
  border-color: rgba(218, 255, 209, 0.25);
  box-shadow: 0 16px 36px -12px rgba(0, 0, 0, 0.5);
}

:global(.dark-mode) .feature-text h4 {
  color: var(--dark-text, #ffffff);
}

:global(.dark-mode) .feature-text p {
  color: var(--dark-text-muted, rgba(255, 255, 255, 0.6));
}

:global(.dark-mode) .feature-icon.color-brand { background: rgba(218, 255, 209, 0.12); color: #DAFFD1; }
:global(.dark-mode) .feature-card:hover .feature-icon.color-brand { background: #DAFFD1; color: #143028; }

:global(.dark-mode) .feature-arrow {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.4);
}

/* ============================================================
   MODAL (réutilisé de Sidebar.vue)
   ============================================================ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content.premium-modal {
  background: var(--light-bg, #FCFAF7);
  border-radius: 20px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  color: var(--light-text, #143028);
}

.dark-mode .modal-content.premium-modal {
  background: var(--dark-bg, #143028);
  color: var(--dark-text, #FFFFFF);
}

.modal-header {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--light-border-dark, rgba(20, 48, 40, 0.1));
}

.dark-mode .modal-header {
  border-bottom-color: var(--dark-border-light, rgba(255, 255, 255, 0.1));
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--light-text-muted, rgba(20, 48, 40, 0.7));
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--light-hover-bg, rgba(20, 48, 40, 0.04));
  color: var(--light-text, #143028);
}

.dark-mode .close-btn {
  color: var(--dark-text-muted, rgba(255, 255, 255, 0.7));
}

.dark-mode .close-btn:hover {
  background: var(--dark-hover-bg, rgba(255, 255, 255, 0.08));
  color: var(--dark-text, #FFFFFF);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.search-container {
  margin-bottom: 20px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--light-text-faint, rgba(20, 48, 40, 0.5));
}

.premium-search-input {
  width: 100%;
  padding: 14px 14px 14px 44px;
  border: 1px solid var(--light-border-dark, rgba(20, 48, 40, 0.1));
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s;
  background: var(--light-card-bg, #FFFFFF);
  color: var(--light-text, #143028);
  box-sizing: border-box;
}

.premium-search-input:focus {
  outline: none;
  border-color: var(--peelo-green, #143028);
  background: white;
  box-shadow: 0 0 0 3px rgba(20, 48, 40, 0.1);
}

.dark-mode .premium-search-input {
  background: var(--dark-card-bg, rgba(255, 255, 255, 0.03));
  border-color: var(--dark-border-light, rgba(255, 255, 255, 0.1));
  color: var(--dark-text, #FFFFFF);
}

.dark-mode .search-icon {
  color: var(--dark-text-faint, rgba(255, 255, 255, 0.5));
}

.modal-subtitle {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--light-text-faint, rgba(20, 48, 40, 0.5));
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.dark-mode .modal-subtitle {
  color: var(--dark-text-faint, rgba(255, 255, 255, 0.5));
}

.agents-list.premium-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.agent-item.premium-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--light-card-bg, #FFFFFF);
  border: 1px solid var(--light-border-dark, rgba(20, 48, 40, 0.1));
  border-radius: 16px;
  transition: all 0.2s;
}

.agent-item.premium-item:hover {
  border-color: var(--peelo-green, #143028);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.dark-mode .agent-item.premium-item {
  background: var(--dark-card-bg, rgba(255, 255, 255, 0.03));
  border-color: var(--dark-border-light, rgba(255, 255, 255, 0.1));
}

.dark-mode .agent-item.premium-item:hover {
  border-color: var(--peelo-accent, #DAFFD1);
}

.agent-avatar-wrapper {
  position: relative;
  margin-right: 16px;
}

.agent-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--light-hover-bg, rgba(20, 48, 40, 0.04));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--peelo-green, #143028);
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-status-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid white;
}

.agent-status-dot.active { background-color: #10B981; }
.agent-status-dot.inactive { background-color: #9CA3AF; }

.dark-mode .agent-status-dot { border-color: var(--dark-bg, #143028); }

.agent-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.agent-name {
  font-weight: 600;
  font-size: 1rem;
  color: var(--light-text, #143028);
  margin-bottom: 4px;
}

.dark-mode .agent-name { color: var(--dark-text, #FFFFFF); }

.agent-email {
  font-size: 0.85rem;
  color: var(--light-text-muted, rgba(20, 48, 40, 0.7));
}

.dark-mode .agent-email {
  color: var(--dark-text-muted, rgba(255, 255, 255, 0.7));
}

.agent-phone-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  margin-top: 3px;
  padding: 3px 10px;
  border-radius: 9999px;
  background: rgba(20, 48, 40, 0.07);
  color: #143028;
  font-size: 0.82rem;
  font-weight: 700;
}
.agent-phone-chip svg { flex-shrink: 0; }
.dark-mode .agent-phone-chip { background: rgba(218, 255, 209, 0.16); color: #DAFFD1; }

.select-agent-btn {
  background: var(--light-hover-bg, rgba(20, 48, 40, 0.08));
  color: var(--peelo-green, #143028);
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.select-agent-btn:hover {
  background: var(--peelo-green, #143028);
  color: var(--peelo-cream, #FCFAF7);
}

.dark-mode .select-agent-btn {
  background: var(--dark-hover-bg, rgba(255, 255, 255, 0.08));
  color: var(--peelo-accent, #DAFFD1);
}

.dark-mode .select-agent-btn:hover {
  background: var(--peelo-accent, #DAFFD1);
  color: var(--peelo-green, #143028);
}

.empty-search {
  text-align: center;
  padding: 32px 0;
  color: #6B7280;
  font-style: italic;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  color: #6B7280;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(20, 48, 40, 0.1);
  border-radius: 50%;
  border-top-color: var(--peelo-green, #143028);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media (max-width: 768px) {
  .app-hub-content {
    padding: 16px;
  }

  .hero-banner {
    flex-direction: column;
    align-items: flex-start;
    padding: 32px 24px;
    text-align: left;
  }

  .hero-title {
    font-size: 1.6rem;
  }

  .hero-visual {
    display: none;
  }

  .hero-actions {
    width: 100%;
  }

  .hero-cta,
  .hero-cta-ghost {
    flex: 1;
    justify-content: center;
  }
}

/* Fonctionnalités réservées au Pro (verrou visuel en Free) */
.pro-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #143028;
  color: #DAFFD1;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 9999px;
  z-index: 2;
}
.feature-card.pro-locked .feature-icon { opacity: 0.85; }
.feature-card.pro-locked .feature-text h4::after {
  content: '🔒';
  font-size: 0.8em;
  margin-left: 6px;
  opacity: 0.6;
}
</style>
